import * as React from 'react';
import { CustomTabRendererProps } from 'types/types.type';
import { Card, Typography, Button, Menu, MenuItem, TextField, Box, Divider, Tooltip } from '@mui/material';
import Grid from '@mui/material/Unstable_Grid2';
import LrGrid, { nullEmptyBottomSort } from 'components/LrGrid';
import LumenLoader from 'components/Loader';
import { useErrorDialog } from 'components/ErrorDialog';
import { Spacing } from 'components/Layout';
import moment from 'moment';
import { formatDollar, unformatNumberString } from 'helpers/formatters';
import ArrowDropDownIcon from '@mui/icons-material/ArrowDropDown';
import CalendarTodayIcon from '@mui/icons-material/CalendarToday';
import { COLORS, STATUS_TYPES } from '../../../../constants';
import { ICellRendererParams, RowSelectedEvent, SizeColumnsToFitGridStrategy } from 'ag-grid-enterprise';
import { useHistory, useLocation } from 'react-router-dom';
import { createUserSubmissionUrl } from 'helpers/submissions';
import { pushPreviousRoutedPathname } from 'helpers/routing';
import SubmissionListItem from 'application/Models/SubmissionListItem';
import KpiBox from './components/KpiBox';
import { useTheme } from '@mui/styles';

/**
 * Priority Submissions View - Shows submissions with upcoming effective dates
 * Includes date range filtering and summary statistics
 */

interface DateRange {
    label: string;
    startDate: moment.Moment;
    endDate: moment.Moment;
}

const predefinedRanges: DateRange[] = [
    {
        label: 'Next 1 Week',
        startDate: moment(),
        endDate: moment().add(1, 'week'),
    },
    {
        label: 'Next 2 Weeks',
        startDate: moment(),
        endDate: moment().add(2, 'weeks'),
    },
    {
        label: 'Next 3 Weeks',
        startDate: moment(),
        endDate: moment().add(3, 'weeks'),
    },
    {
        label: 'Next 4 Weeks',
        startDate: moment(),
        endDate: moment().add(4, 'weeks'),
    },
];

/**
 * Date Range Picker Component
 */
const DateRangePicker: React.FC<{
    selectedRange: DateRange;
    onRangeSelect: (range: DateRange) => void;
}> = ({ selectedRange, onRangeSelect }) => {
    const [anchorEl, setAnchorEl] = React.useState<null | HTMLElement>(null);
    const [customStartDate, setCustomStartDate] = React.useState<string>(moment().format('YYYY-MM-DD'));
    const [customEndDate, setCustomEndDate] = React.useState<string>(moment().add(1, 'week').format('YYYY-MM-DD'));

    const open = Boolean(anchorEl);

    const handleClick = (event: React.MouseEvent<HTMLButtonElement>) => {
        setAnchorEl(event.currentTarget);
    };

    const handleClose = () => {
        setAnchorEl(null);
    };

    const handleRangeSelect = (range: DateRange) => {
        onRangeSelect(range);
        handleClose();
    };

    const handleStartDateChange = (newStartDate: string) => {
        setCustomStartDate(newStartDate);
        if (moment(newStartDate).isAfter(moment(customEndDate))) {
            setCustomEndDate(newStartDate);
        }
    };

    const handleEndDateChange = (newEndDate: string) => {
        setCustomEndDate(newEndDate);
        if (moment(newEndDate).isBefore(moment(customStartDate))) {
            setCustomStartDate(newEndDate);
        }
    };

    const handleCustomRangeApply = () => {
        const customRange: DateRange = {
            label: 'Custom Date Range',
            startDate: moment(customStartDate),
            endDate: moment(customEndDate),
        };
        onRangeSelect(customRange);
        handleClose();
    };

    return (
        <Box display="flex" alignItems="center" gap={0} flexDirection="column">
            <Typography variant="h5" color="textSecondary">
                Effective Date:
            </Typography>
            <Button
                variant="outlined"
                endIcon={<ArrowDropDownIcon />}
                startIcon={<CalendarTodayIcon />}
                onClick={handleClick}
                sx={{ marginLeft: '16px' }}
            >
                {selectedRange.startDate.format('MMM DD')} - {selectedRange.endDate.format('MMM DD')}
            </Button>
            <Menu
                anchorEl={anchorEl}
                open={open}
                onClose={handleClose}
                PaperProps={{
                    style: { minWidth: '300px' },
                }}
            >
                <Spacing height="4px" />
                {predefinedRanges.map((range) => (
                    <MenuItem key={range.label} onClick={() => handleRangeSelect(range)}>
                        <Box
                            display="flex"
                            justifyContent="space-between"
                            alignItems="center"
                            width="100%"
                            paddingBottom="2px"
                            paddingTop="2px"
                        >
                            <Typography variant="body2" style={{ marginBottom: 0 }}>
                                {range.label}
                            </Typography>
                            <Typography variant="caption" style={{ marginBottom: 0 }} color="textSecondary">
                                {range.startDate.format('MMM DD')} - {range.endDate.format('MMM DD, YYYY')}
                            </Typography>
                        </Box>
                    </MenuItem>
                ))}
                <Divider />
                <Box sx={{ padding: '16px', paddingTop: '0' }}>
                    <Typography variant="caption" fontWeight="bold" gutterBottom>
                        Custom Date Range
                    </Typography>
                    <Spacing height="8px" />
                    <Box display="flex" flexDirection="column" alignItems="flex-end" gap={1}>
                        <TextField
                            label="From"
                            type="date"
                            value={customStartDate}
                            onChange={(e) => handleStartDateChange(e.target.value)}
                            margin="dense"
                            size="small"
                            InputLabelProps={{
                                shrink: true,
                                style: { fontSize: '12px' },
                            }}
                            inputProps={{
                                min: moment().format('YYYY-MM-DD'),
                                max: moment().add(8, 'weeks').format('YYYY-MM-DD'),
                                style: { fontSize: '13px' },
                            }}
                            sx={{ minWidth: '224px' }}
                        />
                        <Spacing height="4px" />
                        <TextField
                            label="To"
                            type="date"
                            value={customEndDate}
                            onChange={(e) => handleEndDateChange(e.target.value)}
                            margin="dense"
                            size="small"
                            InputLabelProps={{
                                shrink: true,
                                style: { fontSize: '12px' },
                            }}
                            inputProps={{
                                max: moment().add(8, 'weeks').format('YYYY-MM-DD'),
                                style: { fontSize: '13px' },
                            }}
                            sx={{ minWidth: '224px' }}
                        />
                    </Box>
                    <Button
                        variant="contained"
                        color="secondary"
                        fullWidth
                        onClick={handleCustomRangeApply}
                        sx={{ marginTop: '8px', fontSize: '13px' }}
                    >
                        Apply
                    </Button>
                </Box>
            </Menu>
        </Box>
    );
};

const PrioritySubmissionsView: React.FC<CustomTabRendererProps> = ({ submissions, isLoading, error, emptyText }) => {
    // Navigation hooks
    const history = useHistory();
    const location = useLocation();
    const theme = useTheme();

    const { showError } = useErrorDialog();

    // State for date range selection
    const [selectedRange, setSelectedRange] = React.useState<DateRange>(predefinedRanges[3]); // 4 weeks default
    const [hasLoadedOnce, setHasLoadedOnce] = React.useState(false);
    const [isDataReady, setIsDataReady] = React.useState(false);

    // Track when data has been loaded at least once
    // Set to true once loading completes, regardless of whether there are submissions
    React.useEffect(() => {
        if (!isLoading && submissions !== undefined) {
            setHasLoadedOnce(true);
        }
    }, [isLoading, submissions]);

    React.useEffect(() => {
        if (error) {
            showError({ message: 'Failed to load submissions. Please contact support.' });
        }
    }, [error, showError]);

    // Handle row click to navigate to submission detail
    const handleRowClicked = (event: RowSelectedEvent) => {
        const { node } = event;
        const { id: selectedId } = node.data;

        const selectedSubmission = gridSubmissions.find(({ id }) => id === selectedId) || ({} as SubmissionListItem);

        pushPreviousRoutedPathname(location.pathname, location);
        return history.push(createUserSubmissionUrl(selectedSubmission), { ...location.state });
    };

    const handleRangeSelect = (range: DateRange) => {
        setSelectedRange(range);
    };

    // Filter submissions by effective date. These are used to populate the table and summary metrics.
    const dateFilteredSubmissions = React.useMemo(() => {
        return submissions.filter((sub) => {
            if (!sub.effectiveDate) return false;
            const effectiveDate = moment(sub.effectiveDate);
            return effectiveDate.isBetween(selectedRange.startDate, selectedRange.endDate, 'day', '[]');
        });
    }, [submissions, selectedRange]);

    // Further filter submissions down to statuses relevant for Priority Submissions grid
    const gridSubmissions = React.useMemo(() => {
        return dateFilteredSubmissions.filter((sub) => {
            return [
                STATUS_TYPES.INCOMPLETE_SUBMISSION,
                STATUS_TYPES.READY_FOR_CLEARING,
                STATUS_TYPES.READY_FOR_UNDERWRITING,
                STATUS_TYPES.READY_TO_QUOTE,
            ].includes(sub.statusType.code);
        });
    }, [dateFilteredSubmissions]);

    // Mark data as ready once hasLoadedOnce is true and gridSubmissions has been calculated
    React.useEffect(() => {
        if (hasLoadedOnce) {
            // Use a small timeout to ensure useMemo has completed
            const timer = setTimeout(() => {
                setIsDataReady(true);
            }, 0);
            return () => clearTimeout(timer);
        }
    }, [hasLoadedOnce, gridSubmissions]);

    // Calculate data to populate KPIs
    const summaryData = React.useMemo(() => {
        const totalPremium = gridSubmissions.reduce((sum, sub) => {
            return sum + Number(sub.basePremium || 0);
        }, 0);

        const totalTIV = gridSubmissions.reduce((sum, sub) => {
            // totalInsurableValue is stored as string, need to parse it
            const tiv = unformatNumberString(sub.totalInsurableValue || '0') || 0;
            return sum + tiv;
        }, 0);

        const totalProperty = gridSubmissions.filter((sub) => sub.propertyAvailable).length;
        const totalGL = gridSubmissions.filter((sub) => sub.glAvailable).length;

        // Define workflow statuses with their display values
        const workflowStatuses = [
            { code: STATUS_TYPES.INCOMPLETE_SUBMISSION, displayValue: 'Incomplete' },
            { code: STATUS_TYPES.READY_FOR_CLEARING, displayValue: 'Clearing' },
            { code: STATUS_TYPES.READY_FOR_UNDERWRITING, displayValue: 'Underwriting' },
            { code: STATUS_TYPES.READY_TO_QUOTE, displayValue: 'Ready to Quote' },
        ];

        // Count by status - initialize all workflow statuses to 0
        const statusCounts: Record<string, { displayValue: string; count: number }> = {};
        workflowStatuses.forEach(({ code, displayValue }) => {
            statusCounts[code] = { displayValue, count: 0 };
        });

        // Count actual submissions
        gridSubmissions.forEach((sub) => {
            const statusCode = sub.statusType.code;
            if (statusCounts[statusCode]) {
                statusCounts[statusCode].count++;
            }
        });

        // Count by PQ Score
        const pqScores: Record<string, number> = { '1': 0, '2': 0, '3': 0, X: 0, '-': 0 };
        gridSubmissions.forEach((sub) => {
            const score = sub.pqScore?.toString() || '-';
            if (pqScores[score] !== undefined) {
                pqScores[score]++;
            } else {
                pqScores['-']++;
            }
        });

        // Not quoted counts
        const notQuotedStatuses = [
            STATUS_TYPES.INCOMPLETE_SUBMISSION,
            STATUS_TYPES.READY_FOR_CLEARING,
            STATUS_TYPES.READY_FOR_UNDERWRITING,
            STATUS_TYPES.READY_TO_QUOTE,
        ];
        const notQuotedSubmissions = gridSubmissions.filter((sub) => notQuotedStatuses.includes(sub.statusType.code));

        const cpNotQuoted = notQuotedSubmissions.filter((sub) => sub.propertyAvailable).length;
        const glNotQuoted = notQuotedSubmissions.filter((sub) => sub.glAvailable).length;
        const totalNotQuoted = notQuotedSubmissions.length;

        // Quoted counts
        const quotedStatuses = [
            STATUS_TYPES.QUOTED,
            STATUS_TYPES.BOUND,
            STATUS_TYPES.ISSUED,
            STATUS_TYPES.READY_TO_ISSUE,
            STATUS_TYPES.BIND_REQUEST_REVIEW,
            STATUS_TYPES.REQUOTE_REQUESTED,
        ];
        const quotedSubmissions = dateFilteredSubmissions.filter((sub) => quotedStatuses.includes(sub.statusType.code));

        const totalQuoted = quotedSubmissions.length;

        return {
            totalPremium,
            totalTIV,
            totalProperty,
            totalGL,
            statusCounts,
            pqScores,
            cpNotQuoted,
            glNotQuoted,
            totalNotQuoted,
            totalQuoted,
        };
    }, [dateFilteredSubmissions]);

    // Custom column definitions for Priority Submissions view
    const priorityColumnDefs = React.useMemo(
        () => [
            {
                headerName: 'ID',
                field: 'id',
                width: 125,
                suppressSizeToFit: true,
                cellRenderer: (params: ICellRendererParams) => {
                    return (
                        <>
                            {params.data.isRenewal ? (
                                <Tooltip title={'Renewal Submission.'}>
                                    <span
                                        style={{
                                            borderLeft: `3px solid ${theme.palette.info.main}`,
                                            paddingLeft: '6px',
                                            marginLeft: '-9px',
                                        }}
                                    >
                                        {params.value}
                                    </span>
                                </Tooltip>
                            ) : (
                                params.value
                            )}
                        </>
                    );
                },
            },
            {
                headerName: 'Effective Date',
                field: 'effectiveDate',
                width: 175,
                suppressSizeToFit: true,
                valueFormatter: (params: any) => (params.value ? moment(params.value).format('MM/DD/YYYY') : ''),
            },
            {
                headerName: 'Named Insured',
                field: 'firstNameInsured',
                flex: 1,
            },
            {
                headerName: 'Status',
                valueGetter: (params: any) => {
                    if (
                        params.data.statusType.code === STATUS_TYPES.READY_FOR_UNDERWRITING &&
                        params.data.uwStatus?.statusDisplayName
                    ) {
                        return params.data.uwStatus.statusDisplayName;
                    } else {
                        return params.data.statusType.displayValue;
                    }
                },
                width: 225,
                suppressSizeToFit: true,
            },
            {
                headerName: 'Agent',
                field: 'agentName',
                width: 200,
                suppressSizeToFit: true,
            },
            {
                headerName: 'Base Premium',
                field: 'basePremium',
                width: 175,
                suppressSizeToFit: true,
                valueFormatter: (params: any) => (params.value ? formatDollar(params.value, 0) : '$0'),
            },
            {
                headerName: 'TIV',
                field: 'totalInsurableValue',
                width: 150,
                suppressSizeToFit: true,
                valueFormatter: (params: any) =>
                    params.value ? formatDollar(unformatNumberString(params.value) || 0, 0) : '$0',
            },
            {
                headerName: 'PQ Score',
                field: 'pqScore',
                comparator: nullEmptyBottomSort,
            },
        ],
        []
    );

    if (isLoading || !isDataReady) return <LumenLoader />;

    return (
        <Grid container spacing={2}>
            {/* Submissions Grid with Header and Summary Data */}
            <Grid xs={12}>
                <Card>
                    <Box style={{ maxWidth: '2000px' }}>
                        {/* KPI Summary Dashboard */}
                        <Box sx={{ padding: '0px 24px' }}>
                            <Box
                                sx={{
                                    display: 'flex',
                                    flexWrap: 'wrap',
                                    gap: 4,
                                    alignItems: 'stretch',
                                    justifyContent: 'center',
                                }}
                            >
                                {/* Header with Date Range Selector */}
                                <Box
                                    display="flex"
                                    justifyContent="space-between"
                                    alignItems="center"
                                    sx={{ padding: '0px 24px' }}
                                >
                                    <DateRangePicker selectedRange={selectedRange} onRangeSelect={handleRangeSelect} />
                                </Box>

                                {/* Not Quoted Section */}
                                <KpiBox
                                    title="ACCOUNTS NOT QUOTED"
                                    items={[
                                        { value: summaryData.cpNotQuoted, label: 'CP' },
                                        { value: summaryData.glNotQuoted, label: 'GL' },
                                        { value: summaryData.totalNotQuoted, label: 'Total' },
                                    ]}
                                    accentColor={COLORS.GOLD}
                                />

                                {/* PQ Score Distribution Section */}
                                <KpiBox
                                    title="PQ SCORES"
                                    items={['1', '2', '3', 'X'].map((score) => ({
                                        value: summaryData.pqScores[score] || 0,
                                        label: `PQ ${score}`,
                                    }))}
                                    accentColor={COLORS.INFO_SECONDARY}
                                />

                                {/* Financial Metrics Section */}
                                <KpiBox
                                    title="FINANCIAL"
                                    items={[
                                        { value: formatDollar(summaryData.totalPremium, 0), label: 'Premium' },
                                        { value: formatDollar(summaryData.totalTIV, 0), label: 'TIV' },
                                    ]}
                                    accentColor={COLORS.SUCCESS}
                                />

                                {/* Workflow Status Section */}
                                <KpiBox
                                    title="WORKFLOW STATUS"
                                    items={[
                                        ...[
                                            STATUS_TYPES.INCOMPLETE_SUBMISSION,
                                            STATUS_TYPES.READY_FOR_CLEARING,
                                            STATUS_TYPES.READY_FOR_UNDERWRITING,
                                            STATUS_TYPES.READY_TO_QUOTE,
                                        ]
                                            .map((statusCode) => summaryData.statusCounts[statusCode])
                                            .map((data) => ({
                                                value: data?.count || 0,
                                                label: data?.displayValue || '',
                                            }))
                                            .filter((item) => item.label),
                                        { value: summaryData.totalQuoted, label: 'Quoted' },
                                    ]}
                                    accentColor={COLORS.INFO}
                                />
                            </Box>
                        </Box>
                        <Spacing height="30px" />
                        {gridSubmissions.length === 0 || error ? (
                            <Box style={{ padding: '24px', textAlign: 'center' }}>
                                <Typography variant="h5" gutterBottom>
                                    No Priority Submissions
                                </Typography>
                                <Spacing height="8px" />
                                <Typography variant="body1" color="textSecondary">
                                    {emptyText || 'No submissions with effective dates in the selected range.'}
                                </Typography>
                            </Box>
                        ) : (
                            <LrGrid
                                sizeToFit={true}
                                columnDefs={priorityColumnDefs}
                                rowData={gridSubmissions}
                                rowSelection="single"
                                onRowSelected={handleRowClicked}
                                dense={true}
                                headerHeight={42}
                                initialState={{
                                    sort: {
                                        sortModel: [{ colId: 'effectiveDate', sort: 'asc' }],
                                    },
                                }}
                                height="calc(72vh - 60px)"
                            />
                        )}
                    </Box>
                </Card>
            </Grid>
        </Grid>
    );
};

export default PrioritySubmissionsView;
